/**
 * BirFlyPermit — date helpers.
 * Dates are ISO strings (YYYY-MM-DD). All arithmetic treats them as UTC midnight so the
 * same input produces the same output regardless of the machine's local timezone — this
 * matters because validation must run identically in the browser and in Apps Script.
 */
import type { ISODate, ISOTimestamp } from './types';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Milliseconds since epoch for an ISO date, interpreted as UTC midnight. */
function toUtcMs(iso: ISODate): number {
  return new Date(`${iso}T00:00:00.000Z`).getTime();
}

function fromUtcMs(ms: number): ISODate {
  return new Date(ms).toISOString().slice(0, 10);
}

/** True when `s` is a well-formed, calendar-valid ISO date (YYYY-MM-DD). */
export function isIsoDate(s: string): boolean {
  if (!ISO_DATE_RE.test(s)) return false;
  const ms = toUtcMs(s);
  if (Number.isNaN(ms)) return false;
  // Round-trip check: rejects e.g. 2026-02-30 (JS Date normalises it forward).
  return fromUtcMs(ms) === s;
}

/** Today's date as an ISO string, in UTC. `d` defaults to now; pass a fixed Date in tests. */
export function todayIso(d: Date = new Date()): ISODate {
  return d.toISOString().slice(0, 10);
}

/** `iso` shifted by `n` days (negative goes backwards). */
export function addDays(iso: ISODate, n: number): ISODate {
  return fromUtcMs(toUtcMs(iso) + n * 86_400_000);
}

/** Whole days from `a` to `b` (positive when `b` is later). */
export function daysBetween(a: ISODate, b: ISODate): number {
  return Math.round((toUtcMs(b) - toUtcMs(a)) / 86_400_000);
}

/** Hours from timestamp `a` to timestamp `b` (positive when `b` is later). Fractional. */
export function hoursBetween(a: ISOTimestamp, b: ISOTimestamp): number {
  return (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
}
